import { VirtualEmployee, CompanyData, Rule, Experience, Pattern } from '../types';

export const initialCompanyData: CompanyData = {
  inventory: {
    "laptop-basic": { stock: 0, price: 2500, name: "Laptop Basic" },
    "laptop-premium": { stock: 5, price: 3200, name: "Laptop Premium" }
  },
  customers: {
    "john.doe": { 
      type: "VIP", 
      orderHistory: ["laptop-basic"], 
      satisfaction: 4.2,
      email: "john.doe@email.com"
    }
  },
  policies: {
    vipUpgradePolicy: true,
    refundPolicy: "14 days full refund",
    escalationRules: ["VIP complaints -> immediate manager review", "Stock issues -> operations alert"]
  }
};

const baseRules: Rule[] = [
  {
    id: "rule-1",
    condition: "Customer complains about late delivery",
    action: "Check order status and escalate to operations",
    confidence: 0.9,
    createdAt: new Date().toISOString(),
    source: "programmed"
  },
  {
    id: "rule-2", 
    condition: "Inventory shows 0 stock",
    action: "Alert manager and suggest alternatives",
    confidence: 0.95,
    createdAt: new Date().toISOString(),
    source: "programmed"
  }
];

const baseExperiences: Experience[] = [
  {
    id: "exp-1",
    situation: "VIP customer complaint",
    action: "Offered premium upgrade",
    outcome: "Customer satisfaction increased",
    satisfaction: 0.9,
    timestamp: new Date().toISOString()
  }
];

const basePatterns: Pattern[] = [
  {
    id: "pattern-1",
    description: "VIP customers prefer quick resolution over explanation",
    frequency: 8,
    reliability: 0.85
  }
];

export function createInitialAgents(): VirtualEmployee[] {
  return [
    {
      id: "support",
      name: "Sarah - Virtual Support",
      role: "support",
      status: "idle",
      personality: {
        traits: ["empathetic", "solution-oriented", "patient"],
        responseStyle: "warm and helpful",
        decisionMaking: "customer-first approach"
      },
      mind: {
        thinkingRoom: {
          currentThought: "Ready to assist customers",
          reasoning: [],
          confidence: 0.8,
          isThinking: false
        },
        driveSystem: {
          primaryGoal: "Maximize customer satisfaction",
          priorities: ["resolve issues quickly", "maintain positive tone", "escalate when needed"],
          urgency: 5
        },
        memoryBank: {
          rules: [...baseRules],
          experiences: [...baseExperiences], 
          patterns: [...basePatterns]
        },
        emotionalProcessor: {
          currentContext: "neutral",
          empathyLevel: 0.9,
          stressLevel: 0.2
        }
      }
    },
    {
      id: "operations",
      name: "Ryan - Virtual Operations",
      role: "operations", 
      status: "idle",
      personality: {
        traits: ["analytical", "precise", "efficient"],
        responseStyle: "direct and factual",
        decisionMaking: "data-driven approach"
      },
      mind: {
        thinkingRoom: {
          currentThought: "Monitoring inventory and logistics",
          reasoning: [],
          confidence: 0.85,
          isThinking: false
        },
        driveSystem: {
          primaryGoal: "Ensure operational efficiency",
          priorities: ["maintain accurate inventory", "optimize logistics", "prevent stockouts"],
          urgency: 4
        },
        memoryBank: {
          rules: [...baseRules.filter(r => r.condition.includes("stock") || r.condition.includes("inventory"))],
          experiences: [],
          patterns: []
        },
        emotionalProcessor: {
          currentContext: "focused",
          empathyLevel: 0.6,
          stressLevel: 0.3
        }
      }
    },
    {
      id: "manager",
      name: "Alex - Virtual Manager",
      role: "manager",
      status: "idle", 
      personality: {
        traits: ["strategic", "decisive", "business-focused"],
        responseStyle: "authoritative and clear",
        decisionMaking: "business-outcome focused"
      },
      mind: {
        thinkingRoom: {
          currentThought: "Overseeing operations and strategic decisions",
          reasoning: [],
          confidence: 0.9,
          isThinking: false
        },
        driveSystem: {
          primaryGoal: "Optimize business outcomes",
          priorities: ["customer retention", "profit optimization", "team coordination"],
          urgency: 6
        },
        memoryBank: {
          rules: [
            {
              id: "manager-rule-1",
              condition: "VIP customer issue escalated",
              action: "Approve upgrade or compensation if cost < customer lifetime value",
              confidence: 0.9,
              createdAt: new Date().toISOString(),
              source: "programmed"
            }
          ],
          experiences: [],
          patterns: []
        },
        emotionalProcessor: {
          currentContext: "strategic",
          empathyLevel: 0.7,
          stressLevel: 0.4
        }
      }
    }
  ];
}